import {ReactNode, useCallback, useEffect, useRef, useState} from "react";
import {Turnstile, turnstileAvailable} from "./Turnstile";

export interface TurnstileCheck {
    // The widget, for where the check goes in the form.
    widget: ReactNode;
    // The check couldn't load or didn't pass (or the stage has no site key): the form should say so.
    failed: boolean;
    // The token to send: at once if the check has passed, or once it does, so a form submitted early sends itself rather
    // than asking for another click (its button stays busy meanwhile). Undefined if the check fails or the form goes
    // away first.
    waitForToken(): Promise<string | undefined>;
    // A token works once: after each try, a new widget and a new check.
    reset(): void;
}

// Cloudflare Turnstile's bot check, for a form (sign-in, forgotten password, new support ticket).
export function useTurnstile(): TurnstileCheck {
    const [key, setKey] = useState(0);
    const [failed, setFailed] = useState(false);
    const token = useRef<string | undefined>(undefined);
    const waiting = useRef<((token: string | undefined) => void)[]>([]);

    const settle = useCallback((value: string | undefined) => {
        const resolvers = waiting.current;
        waiting.current = [];
        resolvers.forEach(resolve => resolve(value));
    }, []);

    const onToken = useCallback((value: string | undefined) => {
        // Undefined when a token expires: the widget gets a new one itself.
        token.current = value;
        if (value) {
            setFailed(false);
            settle(value);
        }
    }, [settle]);

    const onError = useCallback(() => {
        token.current = undefined;
        setFailed(true);
        settle(undefined);
    }, [settle]);

    useEffect(() => () => settle(undefined), [settle]);

    const waitForToken = useCallback((): Promise<string | undefined> => {
        if (!turnstileAvailable()) {
            return Promise.resolve(undefined);
        }
        if (token.current) {
            return Promise.resolve(token.current);
        }
        return new Promise(resolve => waiting.current.push(resolve));
    }, []);

    const reset = useCallback(() => {
        token.current = undefined;
        setKey(x => x + 1);
    }, []);

    return {
        widget: <Turnstile key={key} onToken={onToken} onError={onError}/>,
        failed: failed || !turnstileAvailable(),
        waitForToken,
        reset
    };
}
